import type {
    SketchParametricPointHandle,
    SketchParametricSplineCurveDescriptor,
    SketchParametricSplinePointEvaluation,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricSplineEvaluator} from '../core/SketchParametricSplineEvaluator';
import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';
import {readSketchConstraintBranchHintNumber} from '../utils/readSketchConstraintBranchHint';

/** 点在样条关系的最近参数粗搜索分段数。 */
const POINT_ON_SPLINE_SEED_SAMPLE_COUNT = 32;

/**
 * 点关系公式编译器。
 *
 * 职责：统一编译点点重合、点在基础曲线、中点和三点定角关系。
 * 公式只消费 island 的直接目标引用和参数句柄，不读取运行时对象或图遍历顺序。
 */
export class SketchPointConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 当前已迁移的点关系类型。 */
    public readonly relationKinds = [
        'Coincident',
        'Midpoint',
        'ThreePointAngle',
    ] as const;

    /** 真实参数样条求值器。 */
    private readonly splineEvaluator = new SketchParametricSplineEvaluator();

    /** 最近参数粗搜索复用输出点。 */
    private readonly splineSeedPoint: SketchParametricSplinePointEvaluation = {x: 0, y: 0};

    /**
     * 编译点关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        switch (context.constraintState.relationKind) {
            case 'Coincident':
                return this.compileCoincident(context);
            case 'Midpoint':
                return this.compileMidpoint(context);
            case 'ThreePointAngle':
                return this.compileThreePointAngle(context);
            default:
                return 'not-applicable';
        }
    }

    /**
     * 编译点点或点曲线重合关系。
     *
     * 当前公式覆盖点点、点在无限直线、圆/圆弧基础圆、椭圆、抛物线和真实参数样条。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileCoincident(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const equationId = `constraint:${context.constraintState.constraintId}`;
        const directPoints = context.resolveDirectPointHandles();
        const directEntityIds = context.resolveDirectEntityIds();

        if (directPoints.length >= 2) {
            context.system.addEquation(SketchParametricEquationFactory.coincident(
                equationId,
                directPoints[0]!,
                directPoints[1]!,
            ));
            return 'compiled';
        }

        const point = directPoints[0];
        const curveEntityId = directEntityIds[0];

        if (!point || !curveEntityId) {
            /**
             * 无 targetRefs 的历史快照可能只保存两点影响集合。
             * 该回退只接受恰好两个点，禁止从更大的 island 图影响范围猜测直接目标。
             */
            if (context.constraintState.targetPointIds.length === 2) {
                const first = context.pointHandles.get(context.constraintState.targetPointIds[0] ?? '');
                const second = context.pointHandles.get(context.constraintState.targetPointIds[1] ?? '');

                if (first && second) {
                    context.system.addEquation(SketchParametricEquationFactory.coincident(
                        equationId,
                        first,
                        second,
                    ));
                    return 'compiled';
                }
            }

            context.reportUnsupported('重合关系目标无法解析为两点或「点 + 曲线」');
            return 'rejected';
        }

        const line = context.linePointHandles.get(curveEntityId);

        if (line) {
            context.system.addEquation(SketchParametricEquationFactory.pointOnLine(
                equationId,
                point,
                line[0],
                line[1],
            ));
            return 'compiled';
        }

        const center = context.centerPointHandles.get(curveEntityId);
        const radiusParameterId = context.radiusParameterIds.get(curveEntityId);

        if (center && radiusParameterId) {
            context.system.addEquation(SketchParametricEquationFactory.pointOnCircle(
                equationId,
                point,
                center,
                radiusParameterId,
            ));
            return 'compiled';
        }

        const ellipse = context.ellipseGeometryStates.get(curveEntityId);

        if (ellipse) {
            context.system.addEquation(SketchParametricEquationFactory.pointOnEllipse(
                equationId,
                point,
                ellipse.center,
                ellipse.majorPositive,
                ellipse.minorPositive,
            ));
            return 'compiled';
        }

        const parabola = context.parabolaGeometryStates.get(curveEntityId);

        if (parabola) {
            context.system.addEquation(SketchParametricEquationFactory.pointOnParabola(
                equationId,
                point,
                parabola.focal,
                parabola.apex,
            ));
            return 'compiled';
        }

        const spline = context.splineCurveDescriptors.get(curveEntityId);

        if (spline) {
            const persistedParameter = readSketchConstraintBranchHintNumber(
                context.constraintState,
                'point-on-spline',
                'parameter',
            );
            /** 持久参数只选择同一多解支，越界值按当前定义域收口。 */
            const seed = persistedParameter === null
                ? this.resolveNearestSplineParameter(context, spline, point)
                : Math.min(spline.lastParameter, Math.max(spline.firstParameter, persistedParameter));
            const curveParameterId = context.system.addCurveParameter(
                `constraint:${context.constraintState.constraintId}:point-on-spline`,
                seed,
            );
            context.system.addEquation(SketchParametricEquationFactory.pointOnSpline(
                equationId,
                point,
                curveParameterId,
                spline,
            ));
            context.registerResolvedBranchHint(() => ({
                kind: 'point-on-spline',
                parameter: Math.min(
                    spline.lastParameter,
                    Math.max(spline.firstParameter, context.system.read(curveParameterId)),
                ),
            }));
            return 'compiled';
        }

        context.reportUnsupported('重合关系的曲线目标缺少可用参数描述符');
        return 'rejected';
    }

    /**
     * 解析离目标点最近的样条参数，作为点在样条多解分支的稳定 seed。
     *
     * @param context 当前公式编译上下文。
     * @param spline 真实参数样条描述符。
     * @param point 目标点参数句柄。
     * @returns 样条定义域内的最近粗搜索参数。
     */
    private resolveNearestSplineParameter(
        context: SketchConstraintFormulaCompileContext,
        spline: SketchParametricSplineCurveDescriptor,
        point: SketchParametricPointHandle,
    ): number {
        const pointX = context.system.read(point.xId);
        const pointY = context.system.read(point.yId);
        const span = spline.lastParameter - spline.firstParameter;
        const read = (parameterId: string): number => context.system.read(parameterId);
        let bestParameter = spline.firstParameter;
        let bestDistanceSquared = Number.POSITIVE_INFINITY;

        for (let index = 0; index <= POINT_ON_SPLINE_SEED_SAMPLE_COUNT; index += 1) {
            const parameter = spline.firstParameter
                + span * index / POINT_ON_SPLINE_SEED_SAMPLE_COUNT;

            this.splineEvaluator.evaluatePoint(read, spline, parameter, this.splineSeedPoint);
            const deltaX = this.splineSeedPoint.x - pointX;
            const deltaY = this.splineSeedPoint.y - pointY;
            const distanceSquared = deltaX * deltaX + deltaY * deltaY;

            if (distanceSquared < bestDistanceSquared) {
                bestDistanceSquared = distanceSquared;
                bestParameter = parameter;
            }
        }

        return bestParameter;
    }

    /**
     * 编译点在线段中点关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileMidpoint(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const midpoint = context.resolveDirectPointHandles()[0];
        const line = context.resolveDirectEntityIds()
            .map((entityId) => context.linePointHandles.get(entityId))
            .find((handles) => Boolean(handles));

        if (!midpoint || !line) {
            context.reportUnsupported('中点关系需要一个点目标与一条线目标');
            return 'rejected';
        }

        context.system.addEquation(SketchParametricEquationFactory.midpoint(
            `constraint:${context.constraintState.constraintId}`,
            midpoint,
            line[0],
            line[1],
        ));
        return 'compiled';
    }

    /**
     * 编译三点定角关系。
     *
     * 目标顺序固定为“顶点、第一射线端点、第二射线端点”。旋向优先读取关系分支提示，
     * 首次构建才按当前几何选择最近分支；支持大于 π 的跨角幅值。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileThreePointAngle(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const points = context.resolveDirectPointHandles();

        if (points.length < 3) {
            context.reportUnsupported('三点定角关系需要「顶点 + 两射线端点」三个点目标');
            return 'rejected';
        }

        const lockedAngle = context.constraintState.numericValue;

        if (typeof lockedAngle !== 'number' || !Number.isFinite(lockedAngle)) {
            context.reportUnsupported('三点定角关系缺少锁定角度');
            return 'rejected';
        }

        const vertex = points[0]!;
        const firstRay = points[1]!;
        const secondRay = points[2]!;
        const firstX = context.system.read(firstRay.xId) - context.system.read(vertex.xId);
        const firstY = context.system.read(firstRay.yId) - context.system.read(vertex.yId);
        const secondX = context.system.read(secondRay.xId) - context.system.read(vertex.xId);
        const secondY = context.system.read(secondRay.yId) - context.system.read(vertex.yId);
        const currentSignedAngle = Math.atan2(
            firstX * secondY - firstY * secondX,
            firstX * secondX + firstY * secondY,
        );
        const magnitude = Math.abs(lockedAngle);
        const persistedBranchSign = readSketchConstraintBranchHintNumber(
            context.constraintState,
            'angle-direction',
            'branchSign',
        );
        const inferredBranchSign = Math.abs(
            SketchParametricEquationFactory.wrapToPi(currentSignedAngle - magnitude),
        ) <= Math.abs(SketchParametricEquationFactory.wrapToPi(currentSignedAngle + magnitude))
            ? 1
            : -1;
        const branchSign = persistedBranchSign === 1 || persistedBranchSign === -1
            ? persistedBranchSign
            : inferredBranchSign;

        context.system.addEquation(SketchParametricEquationFactory.lineLineAngle(
            `constraint:${context.constraintState.constraintId}`,
            vertex,
            firstRay,
            vertex,
            secondRay,
            magnitude,
            branchSign,
        ));
        context.registerResolvedBranchHint(() => ({kind: 'angle-direction', branchSign}));

        return 'compiled';
    }
}
